import { useAccount } from "#contexts/AccountContext"
import { ClientError } from "#lib/errors"
import { queryKeys } from "#lib/query-keys"
import { showToast } from "#lib/toast"
import { homeFinancialSDK } from "#sdk/sdk"
import type { CreateExpenseCategory } from "@home-finance-manager/shared/types/expense_categories"
import type { CreateExpense } from "@home-finance-manager/shared/types/expenses"
import type { CreatePartner } from "@home-finance-manager/shared/types/partners"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useCategories, useCreateCategory } from "./use-categories"
import { useBulkCreateExpenses, useExpenses } from "./use-expenses"
import { useCreatePartner, usePartners } from "./use-partners"

const DEFAULT_CATEGORIES: Omit<CreateExpenseCategory, "accountId">[] = [
  { name: "Groceries", description: "Food and household items", color: "#3b82f6" },
  { name: "Transportation", description: "Gas, public transport, parking", color: "#8b5cf6" },
  { name: "Housing", description: "Rent, utilities, maintenance", color: "#ef4444" },
  { name: "Entertainment", description: "Movies, dining out, hobbies", color: "#f59e0b" },
  { name: "Healthcare", description: "Medical expenses, insurance", color: "#22c55e" },
  { name: "Shopping", description: "Clothing, electronics, miscellaneous", color: "#ec4899" },
]

export function useIsDashboardEmpty() {
  const { currentAccount, isLoading: accountLoading, isReady } = useAccount()
  const { data: partners = [], isLoading: partnersLoading } = usePartners()
  const { data: categories = [], isLoading: categoriesLoading } = useCategories()
  const { data: expensesData, isLoading: expensesLoading } = useExpenses()

  const expenses = expensesData?.pages?.[0]?.data || []

  const isLoading =
    accountLoading || !isReady || partnersLoading || categoriesLoading || expensesLoading
  const needsPartner = !partnersLoading && partners.length === 0
  const needsCategories = !categoriesLoading && categories.length === 0
  const needsExpenses = !expensesLoading && expenses.length === 0

  const needsOnboarding =
    isReady &&
    !accountLoading &&
    !!currentAccount &&
    !isLoading &&
    (needsPartner || needsCategories || needsExpenses)

  return {
    needsOnboarding,
    isLoading,
    needsPartner,
    needsCategories,
    needsExpenses,
  }
}

export function useOnboardingSetup() {
  const { isReady } = useAccount()
  const queryClient = useQueryClient()
  const createPartner = useCreatePartner()
  const createCategory = useCreateCategory()
  const bulkCreateExpenses = useBulkCreateExpenses()

  return useMutation({
    mutationFn: async (data: {
      partnerName?: string
      partnerEmail?: string
      grossMonthlySalary?: number
      netMonthlySalary?: number
      createDefaultCategories?: boolean
      createSampleExpenses?: boolean
    }) => {
      if (!isReady) {
        throw new ClientError("No account selected", "NO_ACCOUNT", 400)
      }

      const results: {
        partner?: any
        categories: any[]
        expenses: any[]
      } = {
        categories: [],
        expenses: [],
      }

      if (data.partnerName && data.partnerEmail) {
        const nameParts = data.partnerName.trim().split(" ").filter(Boolean)

        if (nameParts.length < 2) {
          throw new ClientError("Please enter both first and last name", "VALIDATION_ERROR", 400)
        }

        const firstName = nameParts[0]
        const lastName = nameParts.slice(1).join(" ")

        if (!firstName || !lastName || !data.partnerEmail.trim()) {
          throw new ClientError(
            "First name, last name, and email are required",
            "VALIDATION_ERROR",
            400
          )
        }

        const grossSalary = data.grossMonthlySalary ?? 0
        const netSalary = data.netMonthlySalary ?? 0

        if (grossSalary < 0 || netSalary < 0) {
          throw new ClientError("Salary values must be positive numbers", "VALIDATION_ERROR", 400)
        }

        const partnerData: CreatePartner = {
          firstName: firstName,
          lastName: lastName,
          email: data.partnerEmail.trim(),
          grossMonthlySalary: grossSalary,
          netMonthlySalary: netSalary,
        }
        results.partner = await createPartner.mutateAsync(partnerData)
      }

      if (data.createDefaultCategories) {
        const categoryPromises = DEFAULT_CATEGORIES.map((cat) => createCategory.mutateAsync(cat))
        results.categories = await Promise.all(categoryPromises)
      }

      const availableCategories =
        results.categories.length > 0
          ? results.categories
          : (await queryClient.fetchQuery({
              queryKey: queryKeys.categories.lists(),
              queryFn: () => homeFinancialSDK.accounts.expenseCategories.getCategories(),
            })) || []

      if (data.createSampleExpenses && availableCategories.length > 0) {
        if (!results.partner) {
          const existingPartners = await queryClient.fetchQuery({
            queryKey: queryKeys.partners.lists(),
            queryFn: () => homeFinancialSDK.accounts.partners.getPartners(),
          })
          if (!existingPartners || existingPartners.length === 0) {
            throw new ClientError(
              "A partner must be created before creating expenses",
              "VALIDATION_ERROR",
              400
            )
          }
        }

        const today = new Date()
        const housingCategory =
          availableCategories.find((c) => c.name === "Housing") ||
          availableCategories[2] ||
          availableCategories[0]
        const transportationCategory =
          availableCategories.find((c) => c.name === "Transportation") ||
          availableCategories[1] ||
          availableCategories[0]

        const sampleExpenses: CreateExpense[] = [
          {
            name: "Weekly Groceries",
            description: "Initial grocery shopping",
            amount: 150,
            recurring: "one-time" as const,
            expenseCategoryId: availableCategories[0]?.id,
            nextPaymentDate: today.toISOString(),
          },
          {
            name: "Monthly Rent",
            description: "Housing payment",
            amount: 1200,
            recurring: "monthly" as const,
            expenseCategoryId: housingCategory?.id || availableCategories[0]?.id,
            nextPaymentDate: today.toISOString(),
          },
          {
            name: "Gas Fill-up",
            description: "Car fuel",
            amount: 60,
            recurring: "one-time" as const,
            expenseCategoryId: transportationCategory?.id || availableCategories[0]?.id,
            nextPaymentDate: today.toISOString(),
          },
        ].filter((exp) => exp.expenseCategoryId)

        if (sampleExpenses.length > 0) {
          await bulkCreateExpenses.mutateAsync(sampleExpenses)
          results.expenses = []
        }
      }

      queryClient.invalidateQueries({ queryKey: queryKeys.partners.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.categories.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all })

      const createdItems = []
      if (results.partner) createdItems.push("partner")
      if (results.categories.length > 0)
        createdItems.push(`${results.categories.length} categories`)
      if (results.expenses.length > 0) createdItems.push(`${results.expenses.length} expenses`)

      if (createdItems.length > 0) {
        showToast.success(`Onboarding setup complete! Created ${createdItems.join(", ")}.`)
      } else {
        showToast.success("Onboarding setup complete!")
      }

      return results
    },
  })
}
